/**
 * `attackRange` (frontend) — the thin bridge from an armed COMBAT.app aim to the **shared**
 * attack geometry. The reach wash + Blast/Line/Cone/Burst templates live in `@lancer-vtt/domain`'s
 * `attackRange`, drawn from the grid-agnostic **`worldFor(state)` spatial port**
 * (`attackReachKeys`/`attackAffectedKeys`/`combatantInReachKeys`) — the SAME seam the server
 * resolver fans out over (`resolveAreaAttack` on `enumerateArea`), so *what the red template shows
 * is exactly who the resolver hits*, on hex as well as square. This file keeps only
 * `weaponRangeForAim`, which reads the frontend `ActionDraft` (the inspector's armed
 * weapon/profile) and resolves it to the frozen `WeaponProfile.range`, and re-exports the domain
 * geometry so callers keep their import site.
 */
import {
  type Combatant,
  type CombatantId,
  type EncounterState,
  type WeaponMode,
  type WeaponProfile,
  type WeaponRange,
  HIDDEN_STATUS,
  attackReachCellList,
  combatantInReachKeys,
  hasStatus,
  isActor,
  latchedWeaponMode,
  primaryReach,
  selectWeaponProfile,
  sideOf,
  weaponInstanceKey,
} from "@lancer-vtt/domain";
import { checkDismountLegality, checkEjectLegality, checkRideDismountLegality } from "@lancer-vtt/rules";
import { attackTargetLosBlocked, type ActionDraft } from "./combatInspector";

export {
  primaryReach,
  isAreaRange,
  // The grid-agnostic port geometry the overlay draws from — the SAME `worldFor(state)` seam the
  // resolver fans out over, so the on-canvas template matches who is hit.
  attackReachKeys,
  attackReachCellList,
  type ReachCell,
  attackAffectedKeys,
  combatantInReachKeys,
  // The LoS-aware "who does the template ACTUALLY hit" twin —
  // `attackAffectedKeys` above stays pure geometry (RAW doesn't shrink the painted template); this is
  // the true hit set once a walled-off target is dropped, plus its footprint-key lookup so a caller
  // can mark the excluded combatant's OWN cells (not just diff two id sets by hand).
  attackAffectedCombatantKeys,
  combatantFootprintKeys,
} from "@lancer-vtt/domain";

/**
 * The **resolved** `WeaponProfile` the armed Skirmish / Barrage aim fires, or `null` when there's no
 * weapon armed. Factored out of {@link weaponRangeForAim} so a
 * caller that needs more than `.range` off the fired profile — e.g. the LoS-exemption tags
 * (`tg_seeking`/`tg_arcing`) `attackTargetLosBlocked` reads — resolves it the SAME way, once, rather
 * than re-deriving the weapon lookup. Same fallback rule as `weaponRangeForAim`: **Barrage** keys
 * strictly to the armed weapon card (no first-weapon fallback); **Skirmish** falls back to the unit's
 * first weapon.
 */
export function weaponProfileForAim(attacker: Combatant, aim: ActionDraft): WeaponProfile | null {
  const weapon = aimWeapon(attacker, aim);
  return weapon ? selectWeaponProfile(weapon, aim.mode === "skirmish" || aim.mode === "barrage" ? aim.profileId || undefined : undefined) : null;
}

/** The frozen weapon the armed Skirmish / Barrage aim names, by {@link weaponProfileForAim}'s rule. */
function aimWeapon(attacker: Combatant, aim: ActionDraft): WeaponProfile | undefined {
  if (aim.mode !== "skirmish" && aim.mode !== "barrage") return undefined;
  const weapons = attacker.base.weapons ?? [];
  const explicit = weapons.find((w) => weaponInstanceKey(w) === aim.weaponId);
  return explicit ?? (aim.mode === "skirmish" ? weapons[0] : undefined);
}

/**
 * The mode a ranged-or-melee weapon is held to this turn, or `undefined` (not yet used this turn,
 * or any other weapon). The shot names it, because the engine's default (melee within Threat) would
 * otherwise refuse a ranged-latched Ram Cannon's shot at an adjacent target.
 */
export function weaponModeThisTurn(args: { state: EncounterState; attackerId: CombatantId; weaponKey: string }): WeaponMode | undefined {
  const { state, attackerId, weaponKey } = args;
  const attacker = state.combatants[attackerId];
  const weapon = (attacker?.base.weapons ?? []).find((w) => weaponInstanceKey(w) === weaponKey);
  if (!attacker || weapon?.eitherMode !== true) return undefined;
  return latchedWeaponMode({ resources: attacker.current.resources, instanceKey: weaponKey, turnOrdinal: state.activation.turnOrdinal });
}

/**
 * A ranged-or-melee weapon's reach this turn (`WeaponProfile.eitherMode`, the Ram Cannon; Core
 * p.297: "not both in the same turn"). Once the engine's latch holds a mode for this turn
 * ({@link latchedWeaponMode}), the other mode's reach goes: melee keeps only the Threat band,
 * ranged drops it. Any other weapon, or an unlatched one, keeps its whole range.
 */
export function rangeThisTurn(args: { attacker: Combatant; weapon: WeaponProfile; range: WeaponRange[]; turnOrdinal: number }): WeaponRange[] {
  const { attacker, weapon, range, turnOrdinal } = args;
  if (weapon.eitherMode !== true) return range;
  const latched = latchedWeaponMode({ resources: attacker.current.resources, instanceKey: weaponInstanceKey(weapon), turnOrdinal });
  if (latched === "melee") return range.filter((r) => r.kind === "threat");
  if (latched === "ranged") return range.filter((r) => r.kind !== "threat");
  return range;
}

/**
 * The `WeaponRange[]` the armed Skirmish / Barrage aim selects, or `null` when there's no weapon /
 * no frozen range. **Barrage** keys its per-shot range gate strictly to the **armed weapon card** —
 * no first-weapon fallback — so the reach wash and target dimming light only once a card is chosen.
 * **Skirmish** keeps the fallback to the unit's first weapon (its "default firing mode", the same one
 * the resolver reaches for when the intent names none).
 */
export function weaponRangeForAim(attacker: Combatant, aim: ActionDraft, turnOrdinal?: number): WeaponRange[] | null {
  // **Cell-pick throws** (`activate_cell` — Smoke/HEX Charges): the reach is the ACTION's own RAW
  // range, frozen at spawn on `base.actionRanges`, not a weapon's. Without this branch the wash
  // never draws for a throw row (`weaponProfileForAim` returns `null` for every non-skirmish/barrage
  // mode), leaving RAW's "within Range 5" with no on-board affordance at all.
  if (aim.mode === "activate_cell") {
    const range = attacker.base.actionRanges?.[aim.actionId];
    return range && range.length > 0 ? range : null;
  }
  // `activate_sites` (directed multi-pick placement) is DELIBERATELY not special-cased here: its
  // wash is adjacency derived live from `legalDirectedDeployCells` (state + the picks so far), a
  // fundamentally different derivation than this frozen-RAW-reach array — Jericho carries no
  // `actionRanges` entry at all (no stated RAW range), so bolting the wash onto this seam the way
  // `activate_cell` does above would be wrong even if it happened to return something. Falls
  // through to the `weaponProfileForAim` branch below, which returns `null` for any
  // non-skirmish/barrage mode — the correct "no wash from THIS seam" answer.
  // `turnOrdinal` (the encounter's) narrows a ranged-or-melee weapon to the mode it has used this
  // turn ({@link rangeThisTurn}); a caller without the state gets the whole range.
  const weapon = aimWeapon(attacker, aim);
  const full = weaponProfileForAim(attacker, aim)?.range;
  const range = weapon && full && turnOrdinal !== undefined ? rangeThisTurn({ attacker, weapon, range: full, turnOrdinal }) : full;
  if (!range || range.length === 0) return null;
  // A Thrown weapon also reaches its thrown value (`tg_thrown`), as the server's range gate reads
  // it (`checkAttackRange`): the Tactical Knife threatens 1 and is thrown 3.
  const thrown = weapon?.tags?.find((t) => t.id === "tg_thrown")?.val ?? 0;
  return thrown > primaryReach(range) ? [...range, { kind: "range", val: thrown }] : range;
}

/**
 * May an armed Skirmish / Barrage `weapon` NOT directly target `target`? The per-target rule of
 * `useTargetingWashes`' `outOfRangeTargetIds` (the UI twin of the server's range, Hidden and LoS
 * walls): out of the reach wash `reach`, Hidden, or with no line of sight. `reach` is `null` for a
 * weapon with no usable reach (the fallback strike), which, like the server's reach-unknown
 * carve-out, gates neither range nor LoS.
 */
export function weaponTargetBlocked(args: { state: EncounterState; attacker: Combatant; target: Combatant; weapon: WeaponProfile | null; reach: Set<string> | null }): boolean {
  const { state, attacker, target, weapon, reach } = args;
  if (reach && !combatantInReachKeys(state, target, reach)) return true;
  if (hasStatus(target.statuses, HIDDEN_STATUS)) return true;
  return reach !== null && attackTargetLosBlocked({ state, attacker, target, weapon });
}

/**
 * The hostile units `attackerId`'s weapon `weaponKey` (an instance key, at its default profile)
 * could target from where the attacker stands: placed actors on the other side that
 * {@link weaponTargetBlocked} lets through. The same rule and the same side filter as the board's
 * `outOfRangeTargetIds` / `legalTargetIds` for a Skirmish armed with that weapon, so the command
 * menu's "N in reach" and the targeting wash cannot disagree. `[]` for an unknown or unplaced
 * attacker or weapon.
 */
export function targetsInReach(state: EncounterState, attackerId: CombatantId, weaponKey: string): string[] {
  const attacker = state.combatants[attackerId];
  if (!attacker?.pos) return [];
  const weapon = (attacker.base.weapons ?? []).find((w) => weaponInstanceKey(w) === weaponKey);
  if (!weapon) return [];
  const profile = selectWeaponProfile(weapon, undefined);
  const turnRange = rangeThisTurn({ attacker, weapon, range: profile.range ?? [], turnOrdinal: state.activation.turnOrdinal });
  const range = turnRange.length > 0 ? turnRange : null;
  const cells = range ? attackReachCellList(state, attacker, range) : null;
  const reach = cells ? new Set(cells.map((c) => c.key)) : null;
  return Object.values(state.combatants)
    .filter((c) => c.id !== attacker.id && isActor(c) && c.pos != null && sideOf(c) !== sideOf(attacker))
    .filter((c) => !weaponTargetBlocked({ state, attacker, target: c, weapon: profile, reach }))
    .map((c) => c.id);
}

/**
 * The reach of the granted action a single-target `activate` aim names, frozen at spawn on
 * `base.actionRanges` (Sniper's Mark's Range 25, an NPC tech feature's Sensors), or `null` for any
 * other aim and for an action with no stated reach. The same number `checkActivateUnitReach` gates the
 * target against. Kept apart from {@link weaponRangeForAim} so the armed-attack readers of that seam
 * (the AoE template, the targeting banner) stay untouched; only the reach wash reads it.
 */
export function activateReachForAim(attacker: Combatant, aim: ActionDraft): WeaponRange[] | null {
  if (aim.mode !== "activate") return null;
  const range = attacker.base.actionRanges?.[aim.actionId];
  return range && range.length > 0 ? range : null;
}

// ── Dismount / Eject landing-cell wash ──────────────────────────────────────────────────────
//
// Neither verb has a weapon or a granted-action range to key `weaponRangeForAim` off — the reach is
// RAW's own bridge rule (Dismount: adjacent; Eject: fly 6), and the "may a pilot actually fit there"
// question is a footprint-occupancy check, not a range one. So this wash is a SEPARATE derivation
// (mirroring `activate_sites`'s own `legalDirectedDeployCells`: a fundamentally different wash gets
// its own function rather than being bolted onto `weaponRangeForAim`), folded into
// `CombatApp.tsx`'s `attackRange` memo the same way `directedDeployWash` is.

/** Named args for {@link legalPilotBridgeCells} (`max-params`-3 discipline). */
export type PilotBridgeCellsArgs = {
  state: EncounterState;
  mechId: CombatantId;
  mode: "dismount" | "eject";
};

/** RAW's own bridge reach, in spaces (Core p.70): Dismount is adjacent-only, Eject is fly 6. */
const PILOT_BRIDGE_REACH: Record<"dismount" | "eject", number> = { dismount: 1, eject: 6 };

/**
 * The legal landing-cell set for an armed Dismount/Eject aim — **built off the real exported wall
 * predicates** (`checkDismountLegality`/`checkEjectLegality`, `@lancer-vtt/rules`), never a
 * hand-rolled distance check: candidates are drawn from the SAME reach-disk geometry the weapon wash
 * and the Sensors wash already use ({@link attackReachCellList}, fed a synthetic `{kind:"range",
 * val: N}` — 1 for Dismount, 6 for Eject, mirroring `combatInspector.ts`'s `sensorReachCells`), then
 * each candidate is filtered through the mech's own real legality check for that cell — so the wash
 * can never independently drift from the wall it mirrors. This deliberately runs the WHOLE predicate
 * (including the "already Unpiloted" / Eject-lockout preconditions, which are position-independent
 * and so reject every candidate uniformly), not just the footprint-fit half: an Eject-locked-out mech
 * legitimately has nowhere left to eject to, and an empty wash says so honestly. `null` when the mech
 * is unknown/unplaced (no wash to draw); `[]` (as a `Set`) is a real, meaningful "no legal cell right
 * now" answer, distinct from `null`'s "ungated."
 */
export function legalPilotBridgeCells(args: PilotBridgeCellsArgs): Set<string> | null {
  const { state, mechId, mode } = args;
  const mech = state.combatants[mechId];
  if (!mech || !mech.pos) return null;
  const candidates = attackReachCellList(state, mech, [{ kind: "range", val: PILOT_BRIDGE_REACH[mode] }]);
  if (!candidates) return null;
  const check = mode === "eject" ? checkEjectLegality : checkDismountLegality;
  const legal = new Set<string>();
  for (const { key, cell } of candidates) {
    if (check(state, mechId, cell).ok) legal.add(key);
  }
  return legal;
}

// ── Ride-Dismount landing-cell wash (RAW Core p.70) ──────────────────────────────────────────
//
// Structurally {@link legalPilotBridgeCells}'s sibling, not a variant of it: `checkRideDismountLegality`
// takes a RIDER id (not a mech id), and measures adjacency from the rider's CARRIER, not the rider
// itself — but a live `Riding` marker always co-locates the two (`rideAttachSteps`'s own `move` step
// plants the rider directly on the carrier's cell), so the rider's OWN `.pos` is exactly the anchor
// {@link checkRideDismountLegality}'s adjacency check measures from. Candidates are drawn from the
// SAME reach-disk geometry every other wash in this file uses ({@link attackReachCellList}, fed
// RAW's own adjacent-only reach — mirroring Dismount's `PILOT_BRIDGE_REACH.dismount`), then each is
// filtered through the real exported wall predicate — never a hand-rolled distance check.

/** Named args for {@link legalRideDismountCells} (`max-params`-3 discipline). */
export type RideDismountCellsArgs = {
  state: EncounterState;
  riderId: CombatantId;
};

/**
 * The legal landing-cell set for an armed Ride-Dismount aim — built off the real exported
 * `checkRideDismountLegality` (`@lancer-vtt/rules`), never a hand-rolled adjacency check. Runs the
 * WHOLE predicate per candidate (including the position-independent "actually Riding a resolvable
 * carrier" precondition — a rider who isn't currently Riding anyone gets the uniformly EMPTY set,
 * not `null`, mirroring `legalPilotBridgeCells`'s own Eject-lockout precedent), so an empty wash
 * here is a real, meaningful answer, not a sign of a broken caller. `null` when the rider itself is
 * unknown/unplaced (no wash to draw at all).
 */
export function legalRideDismountCells(args: RideDismountCellsArgs): Set<string> | null {
  const { state, riderId } = args;
  const rider = state.combatants[riderId];
  if (!rider || !rider.pos) return null;
  const candidates = attackReachCellList(state, rider, [{ kind: "range", val: 1 }]);
  if (!candidates) return null;
  const legal = new Set<string>();
  for (const { key, cell } of candidates) {
    if (checkRideDismountLegality(state, riderId, cell).ok) legal.add(key);
  }
  return legal;
}
